// src/components/ModalFormMov.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { putMov as modifMov } from "../helpers/rutaMovs";
import { getHoms } from "../helpers/rutaHoms";
import { getClienteByCodigo, buscarClientes } from "../helpers/rutaClientes";

import { Modal, Button, Form, ListGroup, Spinner } from "react-bootstrap";
import "animate.css";

const toDateInput = (v) => {
  if (!v) return "";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "" : d.toISOString().slice(0, 10);
};

const ModalFormMov = ({ mov, handleClose, show }) => {
  const [showSuccess, setShowSuccess] = useState(false);

  const [formValues, setFormValues] = useState({
    codmov: "",
    codcli: "",
    codhom: "",
    fecmov: "",
    leyenda: "",
    usuario1: "",
  });

  const [homs, setHoms] = useState({ data: [], loading: true });

  // solo UI (no se persiste)
  const [ui, setUi] = useState({
    clienteDesc: "",
    homDesc: "",
  });

  /* ---------------- HOMs ---------------- */
  useEffect(() => {
    getHoms()
      .then((list) => setHoms({ data: Array.isArray(list) ? list : [], loading: false }))
      .catch(() => setHoms({ data: [], loading: false }));
  }, []);

  const homMap = useMemo(() => {
    const map = {};
    (homs.data || []).forEach((h) => (map[String(h.codhom)] = h?.hom || ""));
    return map;
  }, [homs.data]);

  /* ---------------- cliente por código ---------------- */
  const [buscandoCod, setBuscandoCod] = useState(false);
  const reqSeqCod = useRef(0);

  const fetchClienteByCod = useCallback(async (codcli) => {
    const mySeq = ++reqSeqCod.current;
    setBuscandoCod(true);

    try {
      const resp = await getClienteByCodigo(codcli);
      if (mySeq !== reqSeqCod.current) return;

      // soporte a ambos formatos: resp.cliente o resp directo
      const cli = resp?.cliente ?? resp;
      setUi((p) => ({ ...p, clienteDesc: cli?.razonsocial || "No encontrado" }));
    } catch (err) {
      if (mySeq !== reqSeqCod.current) return;

      const status = err?.response?.status;
      const msg =
        status === 401 || status === 403
          ? "Sin permiso (rol)"
          : status === 404
          ? "No encontrado"
          : "Error consultando cliente";

      setUi((p) => ({ ...p, clienteDesc: msg }));
    } finally {
      if (mySeq === reqSeqCod.current) setBuscandoCod(false);
    }
  }, []);

  /* ---------------- modal buscar cliente (0 -> modal) ---------------- */
  const [showCliModal, setShowCliModal] = useState(false);
  const [qCli, setQCli] = useState("");
  const [cliOpts, setCliOpts] = useState([]);
  const [loadingCli, setLoadingCli] = useState(false);
  const [selIdx, setSelIdx] = useState(0);
  const reqSeqQ = useRef(0);
  const inputRef = useRef(null);

  const openCliModal = useCallback(() => {
    setShowCliModal(true);
    setQCli("");
    setCliOpts([]);
    setSelIdx(0);
    setTimeout(() => inputRef.current?.focus(), 50);
  }, []);

  const closeCliModal = useCallback(() => setShowCliModal(false), []);

  const seleccionarCliente = useCallback(
    (c) => {
      if (!c) return;
      setFormValues((p) => ({ ...p, codcli: String(c.codcli) }));
      setUi((p) => ({ ...p, clienteDesc: c.razonsocial || "" }));
      closeCliModal();
    },
    [closeCliModal]
  );

  const onModalKeyDown = (e) => {
    if (e.key === "Escape") {
      e.preventDefault();
      closeCliModal();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setSelIdx((i) => Math.min(i + 1, Math.max(0, cliOpts.length - 1)));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setSelIdx((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const pick = cliOpts[selIdx] || cliOpts[0];
      if (pick) seleccionarCliente(pick);
    }
  };

  // debounce búsqueda textual en modal
  useEffect(() => {
    if (!showCliModal) return;

    const q = qCli.trim();
    if (q.length < 2) {
      setCliOpts([]);
      setSelIdx(0);
      return;
    }

    const mySeq = ++reqSeqQ.current;
    const t = setTimeout(async () => {
      setLoadingCli(true);
      try {
        const list = await buscarClientes(q, 50);
        if (mySeq !== reqSeqQ.current) return;

        const arr = Array.isArray(list) ? list : [];
        setCliOpts(arr);
        setSelIdx(0);
      } catch {
        if (mySeq !== reqSeqQ.current) return;
        setCliOpts([]);
        setSelIdx(0);
      } finally {
        if (mySeq === reqSeqQ.current) setLoadingCli(false);
      }
    }, 250);

    return () => clearTimeout(t);
  }, [qCli, showCliModal]);

  const buscarCliente = useCallback(
    async (rawOverride) => {
      const raw = String(rawOverride ?? formValues.codcli ?? "").trim();

      // ✅ Regla Fox: vacío o "0" -> modal
      if (raw === "" || raw === "0") {
        openCliModal();
        return;
      }

      const codcli = Number(raw);
      if (!Number.isFinite(codcli) || codcli <= 0) {
        openCliModal();
        return;
      }

      await fetchClienteByCod(codcli);
    },
    [formValues.codcli, fetchClienteByCod, openCliModal]
  );

  /* ---------------- carga inicial del modal ---------------- */
  useEffect(() => {
    if (!show || !mov?._id) return;

    setFormValues({
      codmov: mov.codmov || "",
      codcli: mov.codcli || "",
      codhom: mov.codhom || "",
      fecmov: toDateInput(mov.fecmov),
      leyenda: mov.leyenda || "",
      usuario1: mov.usuario1 || "",
    });

    setUi((p) => ({
      ...p,
      homDesc: homMap[String(mov.codhom)] || "",
      clienteDesc: "",
    }));

    if (mov.codcli) {
      fetchClienteByCod(Number(mov.codcli));
    }
  }, [show, mov?._id]); // intencional: carga al abrir / cambiar registro

  // recalcular homDesc cuando cambia codhom o llega el catálogo
  useEffect(() => {
    setUi((p) => ({ ...p, homDesc: homMap[String(formValues.codhom)] || "" }));
  }, [formValues.codhom, homMap]);

  /* ---------------- handlers ---------------- */
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues((p) => ({ ...p, [name]: value }));
  };

  const handleHom = (e) => {
    const cod = e.target.value;
    setFormValues((p) => ({ ...p, codhom: cod }));
    setUi((p) => ({ ...p, homDesc: homMap[String(cod)] || "" }));
  };

  /* ---------------- submit ---------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const payload = { ...formValues };
      delete payload.codmov; // no se edita

      await modifMov(mov._id, payload);

      setShowSuccess(true);
      setTimeout(() => {
        setShowSuccess(false);
        handleClose();
      }, 900);
    } catch (error) {
      console.error("❌ Error al actualizar movimiento:", error?.response?.data || error);
      alert("No se pudo guardar. Revisá consola / Network.");
    }
  };

  return (
    <>
      <Modal show={show} onHide={handleClose}>
        <form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>Modificar Movimiento</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <div className="form-row">
              <div className="form-group mt-2 col-sm-3">
                <label>Código</label>
                <input className="form-control" value={formValues.codmov} disabled />
              </div>

              <div className="form-group mt-2 col-sm-4">
                <label>Fecha</label>
                <input
                  type="date"
                  className="form-control"
                  name="fecmov"
                  value={formValues.fecmov}
                  onChange={handleChange}
                />
              </div>

              <div className="form-group mt-2 col-sm-5">
                <label>Cod. Cliente</label>

                {/* ✅ igual que en alta: input + botón + regla 0 */}
                <div className="input-group">
                  <input
                    type="number"
                    className="form-control"
                    name="codcli"
                    value={formValues.codcli}
                    onChange={handleChange}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        buscarCliente(e.currentTarget.value);
                      }
                    }}
                    onBlur={(e) => {
                      const v = String(e.currentTarget.value ?? "").trim();
                      if (v === "0") buscarCliente(v);
                      else if (v) buscarCliente(v); // si dejó un número, lo buscamos
                      else setUi((p) => ({ ...p, clienteDesc: "" }));
                    }}
                    min="0"
                  />

                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={() => buscarCliente()}
                    title="Buscar (0 abre modal)"
                    style={{
                      width: 44,
                      height: 38,
                      padding: 0,
                      fontSize: "1rem",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {buscandoCod ? "…" : "🔎"}
                  </button>
                </div>

                <small className="text-muted">
                  Tip: poné <b>0</b> y Enter para buscar por apellido.
                </small>
              </div>
            </div>

            <div className="form-group mt-2">
              <label>Cliente (desc.)</label>
              <input className="form-control" value={ui.clienteDesc} readOnly />
            </div>

            <div className="form-row mt-2">
              <div className="form-group col-sm-6">
                <label>HOM</label>
                <select className="form-control" value={formValues.codhom} onChange={handleHom} required>
                  <option value="">Elija opción</option>
                  {(homs.data || [])
                    .filter((x) => x?.activo !== false)
                    .sort((a, b) => (a.codhom ?? 0) - (b.codhom ?? 0))
                    .map((h) => (
                      <option key={h._id} value={h.codhom}>
                        {h.codhom} - {h.hom}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-group col-sm-6">
                <label>HOM (desc.)</label>
                <input className="form-control" value={ui.homDesc} readOnly />
              </div>
            </div>

            <div className="form-row mt-2">
              <div className="form-group col-sm-4">
                <label>Usuario</label>
                <input
                  type="text"
                  className="form-control"
                  name="usuario1"
                  value={formValues.usuario1}
                  onChange={handleChange}
                />
              </div>
            </div>

            <div className="form-group mt-2">
              <label>Leyenda</label>
              <textarea
                className="form-control"
                name="leyenda"
                rows={4}
                value={formValues.leyenda}
                onChange={handleChange}
              />
            </div>
          </Modal.Body>

          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" variant="dark">
              Guardar
            </Button>
          </Modal.Footer>
        </form>
      </Modal>

      {/* MODAL BUSCAR CLIENTE */}
      <Modal show={showCliModal} onHide={closeCliModal} centered backdrop="static">
        <Modal.Header closeButton>
          <Modal.Title>Buscar cliente</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form.Group>
            <Form.Label>Apellido / Razón social</Form.Label>
            <Form.Control
              ref={inputRef}
              value={qCli}
              placeholder="Ej: garc"
              onChange={(e) => setQCli(e.target.value)}
              onKeyDown={onModalKeyDown}
            />
            <Form.Text className="text-muted">↑ ↓ para moverte • Enter selecciona • Esc cierra</Form.Text>
          </Form.Group>

          <div className="mt-3" style={{ maxHeight: 320, overflowY: "auto" }}>
            {loadingCli && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner animation="border" size="sm" /> Buscando…
              </div>
            )}

            {!loadingCli && qCli.trim().length >= 2 && !cliOpts.length && <div className="text-muted">Sin resultados.</div>}

            {!!cliOpts.length && (
              <ListGroup>
                {cliOpts.map((c, idx) => (
                  <ListGroup.Item
                    key={c.codcli}
                    action
                    active={idx === selIdx}
                    onClick={() => seleccionarCliente(c)}
                  >
                    <b>{c.codcli}</b> — {c.razonsocial}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeCliModal}>
            Cerrar
          </Button>
          <Button variant="primary" onClick={() => seleccionarCliente(cliOpts[selIdx] || cliOpts[0])} disabled={!cliOpts.length}>
            Seleccionar
          </Button>
        </Modal.Footer>
      </Modal>

      {/* SUCCESS */}
      <Modal
        show={showSuccess}
        centered
        backdrop="static"
        keyboard={false}
        dialogClassName="animate__animated animate__fadeInDown"
      >
        <Modal.Body className="text-center">
          <div style={{ fontSize: "3rem" }}>🖐</div>
          <h5 className="mt-2">¡Cambios guardados correctamente!</h5>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default ModalFormMov;